import React from "react"
import styled from "styled-components"
import colors from "@constants/colors"
import breakpoints from "@constants/breakpoints"
import {
  body14Styles,
  paragraphStyles,
  paragraph18Styles,
  paragraph20Styles,
  subtextStyles,
} from "@constants/typography"
import Button from "@components/buttons/Button"
import Media from "@components/Media"
import { Container as GifContainer, GifWrapper } from "@components/Media/Gif"
import { Container as ImageContainer } from "@components/Media/Image"
import {
  Container as VideoContainer,
  VideoWrapper,
} from "@components/Media/Video"
import {
  Container as WistiaContainer,
  Content as WistiaWrapper,
} from "@components/Media/Wistia"
import {
  Container as YoutubeContainer,
  Content as YoutubeWrapper,
} from "@components/Media/YouTube"
import {
  Container as MuxContainer,
  Content as MuxWrapper,
} from "@components/Media/Mux"
import { Container as ImageHelperContainer } from "@components/ImageHelper"

const Container = styled.div`
  @media ${breakpoints.laptop} {
    display: flex;
  }
`

const ContentWrapper = styled.div`
  flex-direction: column;
  display: flex;
  padding-top: 40px;

  ${GifContainer}, ${ImageContainer}, ${VideoContainer}, ${WistiaContainer}, ${YoutubeContainer}, ${MuxContainer} {
    padding: 0;
  }

  @media ${breakpoints.tablet} {
    padding-top: 61px;

    ${GifContainer}, ${ImageContainer}, ${VideoContainer}, ${WistiaContainer}, ${YoutubeContainer}, ${MuxContainer} {
      padding: 0;
    }

    ${GifWrapper}, ${ImageHelperContainer}, ${VideoWrapper}, ${WistiaWrapper}, ${YoutubeWrapper}, ${MuxWrapper} {
      width: 100%;
      height: calc(100vh - 120px);
    }
  }

  @media ${breakpoints.laptop} {
    width: 100%;
    padding-top: 54px;

    ${GifContainer}, ${ImageContainer}, ${VideoContainer}, ${WistiaContainer}, ${YoutubeContainer}, ${MuxContainer} {
      padding: 0;
    }

    ${GifWrapper}, ${ImageHelperContainer}, ${VideoWrapper}, ${WistiaWrapper}, ${YoutubeWrapper}, ${MuxWrapper} {
      width: 100%;
      height: calc(10 / 12 * 100vw * 0.51);
    }
  }
`

const Text = styled.div`
  margin-bottom: 31px;
  padding: 0 20px;

  h6 {
    ${body14Styles}
    margin: 0 0 34px;
  }

  p {
    ${paragraph20Styles}
    margin: 0 0 34px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  p.finePrint {
    ${subtextStyles}
    margin: 0 0 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    margin: 0 0 34px;
    padding-left: 18px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    color: ${colors.mediumDarkGray};

    p {
      ${paragraphStyles}
      color: ${colors.mediumDarkGray};
    }
  }

  @media ${breakpoints.tablet} {
    padding: 0 calc(2.5 / 12 * 100vw) 0 calc(1 / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    margin-bottom: 22px;
    padding: 0;

    h6 {
      margin-bottom: 41px;
    }

    p {
      margin-bottom: 41px;
      font-size: 22px;
      line-height: 33px;
    }

    p.finePrint {
      ${subtextStyles}
      margin-bottom: 12px;
    }

    ul {
      margin-bottom: 41px;
    }

    li {
      p {
        ${paragraph18Styles}
        color: ${colors.mediumDarkGray};
      }
    }
  }
`

const DownloadWrapper = styled.div`
  padding: 0 20px;

  @media ${breakpoints.laptop} {
    width: 320px;
    padding: 0;
  }
`

const Download = styled.a`
  width: 100%;

  @media ${breakpoints.laptop} {
    width: 320px;
  }
`

const InstallationBlock = ({ text, media, image }) => {
  return (
    <Container>
      <ContentWrapper>
        <Text>{text}</Text>
        {media && (
          <Media
            type={media.type}
            caption={media.caption}
            mobileImage={media.mobileImage?.file?.url}
            image={media.image?.file?.url}
            mobileGif={media.mobileGif?.file?.url}
            gif={media.gif?.file?.url}
            muxVideo={media.muxVideo}
            muxMobileVideo={media.muxMobileVideo}
            youtubeVideoId={media.youtubeVideoId}
            wistiaVideoId={media.wistiaVideoId}
            mobileVideoUrl={media.mobileVideoUrl}
            videoUrl={media.videoUrl}
            videoThumbnail={media.videoThumbnail}
            customVideoThumbnail={media.customVideoThumbnail?.file?.url}
            autoplayVideo={media.autoplayVideo}
          />
        )}
        {image && (
          <DownloadWrapper>
            <Download href={image?.file?.url || ""} download target="_blank">
              <Button text="Download" />
            </Download>
          </DownloadWrapper>
        )}
      </ContentWrapper>
    </Container>
  )
}

export default InstallationBlock
